/**
 * Portal Admin Management Component
 *
 * Manages portal admin users (admin.admin_users table)
 * These are users who can access the admin portal itself
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Grid,
  InputAdornment,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  CheckCircle as ActiveIcon,
  Block as InactiveIcon,
  AdminPanelSettings as SuperAdminIcon,
  ContentCopy as CopyIcon,
  Visibility as ViewIcon,
  VisibilityOff as HideIcon,
} from '@mui/icons-material';
import { portalAdminAPI, PortalAdmin, PortalAdminCreate, PortalAdminUpdate } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const PortalAdminManagement: React.FC = () => {
  // State
  const [admins, setAdmins] = useState<PortalAdmin[]>([]);
  const [filteredAdmins, setFilteredAdmins] = useState<PortalAdmin[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState<PortalAdmin | null>(null);
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');

  // Form state
  const [formData, setFormData] = useState<PortalAdminCreate & { password?: string }>({
    username: '',
    email: '',
    full_name: '',
    password: '',
    is_super_admin: false,
    can_create_tenants: false,
    can_delete_tenants: false,
    can_modify_resources: false,
    can_view_all_metrics: true,
  });

  const { user } = useAuth();
  const isSuperAdmin = user?.is_super_admin;

  // Load admins
  const loadAdmins = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await portalAdminAPI.list();
      setAdmins(data.users || []);
      setFilteredAdmins(data.users || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load portal admins');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAdmins();
  }, []);

  // Filter admins based on search
  useEffect(() => {
    let filtered = [...admins];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (admin) =>
          admin.email?.toLowerCase().includes(query) ||
          admin.username?.toLowerCase().includes(query)
      );
    }

    setFilteredAdmins(filtered);
  }, [admins, searchQuery]);

  // Handle form input changes
  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      username: '',
      email: '',
      full_name: '',
      password: '',
      is_super_admin: false,
      can_create_tenants: false,
      can_delete_tenants: false,
      can_modify_resources: false,
      can_view_all_metrics: true,
    });
    setEditingAdmin(null);
    setTempPassword(null);
  };

  // Open create dialog
  const handleOpenCreate = () => {
    resetForm();
    setCreateDialogOpen(true);
  };

  // Open edit dialog
  const handleOpenEdit = (admin: PortalAdmin) => {
    setEditingAdmin(admin);
    setFormData({
      username: admin.username,
      email: admin.email,
      full_name: admin.full_name || '',
      password: '',
      is_super_admin: admin.is_super_admin,
      can_create_tenants: admin.can_create_tenants,
      can_delete_tenants: admin.can_delete_tenants,
      can_modify_resources: admin.can_modify_resources,
      can_view_all_metrics: admin.can_view_all_metrics,
    });
    setEditDialogOpen(true);
  };

  // Handle create admin
  const handleCreateAdmin = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const payload: PortalAdminCreate = {
        username: formData.username.trim(),
        email: formData.email.trim(),
        full_name: formData.full_name?.trim() || undefined,
        is_super_admin: formData.is_super_admin,
        can_create_tenants: formData.can_create_tenants,
        can_delete_tenants: formData.can_delete_tenants,
        can_modify_resources: formData.can_modify_resources,
        can_view_all_metrics: formData.can_view_all_metrics,
      };

      // Only include password if provided
      if (formData.password && formData.password.trim()) {
        payload.password = formData.password.trim();
      }

      const result = await portalAdminAPI.create(payload);

      if (result.temporary_password) {
        setTempPassword(result.temporary_password);
        setSuccess('Portal admin created successfully. Please save the temporary password.');
      } else {
        setSuccess('Portal admin created successfully.');
        setCreateDialogOpen(false);
        resetForm();
      }

      await loadAdmins();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create portal admin');
    } finally {
      setLoading(false);
    }
  };

  // Handle update admin
  const handleUpdateAdmin = async () => {
    if (!editingAdmin) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const updates: PortalAdminUpdate = {};

      if (formData.username !== editingAdmin.username) {
        updates.username = formData.username.trim();
      }
      if (formData.email !== editingAdmin.email) {
        updates.email = formData.email.trim();
      }
      if (formData.full_name !== (editingAdmin.full_name || '')) {
        updates.full_name = formData.full_name?.trim() || undefined;
      }
      if (formData.password && formData.password.trim()) {
        updates.password = formData.password.trim();
      }
      if (formData.is_super_admin !== editingAdmin.is_super_admin) {
        updates.is_super_admin = formData.is_super_admin;
      }
      if (formData.can_create_tenants !== editingAdmin.can_create_tenants) {
        updates.can_create_tenants = formData.can_create_tenants;
      }
      if (formData.can_delete_tenants !== editingAdmin.can_delete_tenants) {
        updates.can_delete_tenants = formData.can_delete_tenants;
      }
      if (formData.can_modify_resources !== editingAdmin.can_modify_resources) {
        updates.can_modify_resources = formData.can_modify_resources;
      }
      if (formData.can_view_all_metrics !== editingAdmin.can_view_all_metrics) {
        updates.can_view_all_metrics = formData.can_view_all_metrics;
      }

      if (Object.keys(updates).length === 0) {
        setError('No changes to save');
        setLoading(false);
        return;
      }

      await portalAdminAPI.update(editingAdmin.id, updates);

      setSuccess('Portal admin updated successfully');
      setEditDialogOpen(false);
      resetForm();
      await loadAdmins();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update portal admin');
    } finally {
      setLoading(false);
    }
  };

  // Handle toggle admin status
  const handleToggleStatus = async (admin: PortalAdmin) => {
    setLoading(true);
    setError(null);
    try {
      await portalAdminAPI.update(admin.id, { is_active: !admin.is_active });
      setSuccess(`Admin ${admin.is_active ? 'deactivated' : 'activated'} successfully`);
      await loadAdmins();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to toggle admin status');
    } finally {
      setLoading(false);
    }
  };

  // Handle delete admin
  const handleDeleteAdmin = async (adminId: string, username: string) => {
    if (!window.confirm(`Are you sure you want to delete admin "${username}"? This action cannot be undone.`)) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await portalAdminAPI.delete(adminId);
      setSuccess('Portal admin deleted successfully');
      await loadAdmins();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete portal admin');
    } finally {
      setLoading(false);
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSuccess('Password copied to clipboard');
  };

  // Validate form
  const isFormValid = () => {
    return (
      formData.username.trim().length >= 3 &&
      formData.email.trim() !== '' &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
    );
  };

  // Permission badge component
  const PermissionBadge: React.FC<{ enabled: boolean; label: string }> = ({ enabled, label }) => (
    <Chip
      label={label}
      size="small"
      color={enabled ? 'primary' : 'default'}
      variant={enabled ? 'filled' : 'outlined'}
      sx={{ mr: 0.5, mb: 0.5, fontSize: '0.7rem' }}
    />
  );

  return (
    <Box>
      {/* Action Bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Manage users who can access the admin portal
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadAdmins}>
            Refresh
          </Button>
          {isSuperAdmin && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
              Add Admin
            </Button>
          )}
        </Box>
      </Box>

      {/* Alerts */}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search by username or email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Admins Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Username</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Permissions</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last Login</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && admins.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredAdmins.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="text.secondary">
                    {searchQuery
                      ? 'No admins found matching the search'
                      : 'No portal admins found'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredAdmins.map((admin) => (
                <TableRow key={admin.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {admin.is_super_admin && (
                        <Tooltip title="Super Admin">
                          <SuperAdminIcon color="error" fontSize="small" />
                        </Tooltip>
                      )}
                      <Typography variant="body2" fontWeight="bold">
                        {admin.username}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{admin.email}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                      {admin.is_super_admin ? (
                        <Chip label="Super Admin" size="small" color="error" />
                      ) : (
                        <>
                          <PermissionBadge enabled={admin.can_create_tenants} label="Create Tenants" />
                          <PermissionBadge enabled={admin.can_delete_tenants} label="Delete Tenants" />
                          <PermissionBadge enabled={admin.can_modify_resources} label="Modify Resources" />
                          <PermissionBadge enabled={admin.can_view_all_metrics} label="View Metrics" />
                        </>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={admin.is_active ? <ActiveIcon /> : <InactiveIcon />}
                      label={admin.is_active ? 'Active' : 'Inactive'}
                      color={admin.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {admin.last_login
                      ? new Date(admin.last_login).toLocaleDateString()
                      : 'Never'}
                  </TableCell>
                  <TableCell>
                    {isSuperAdmin && (
                      <>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenEdit(admin)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={admin.is_active ? 'Deactivate' : 'Activate'}>
                          <IconButton
                            size="small"
                            color={admin.is_active ? 'warning' : 'success'}
                            onClick={() => handleToggleStatus(admin)}
                            disabled={admin.email === user?.email}
                          >
                            {admin.is_active ? <InactiveIcon /> : <ActiveIcon />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteAdmin(admin.id, admin.username)}
                            disabled={admin.email === user?.email}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create Admin Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => {
          setCreateDialogOpen(false);
          resetForm();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Portal Admin</DialogTitle>
        <DialogContent>
          {tempPassword ? (
            <Box sx={{ mt: 2 }}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Save this temporary password! It will not be shown again.
              </Alert>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Temporary Password
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                      fullWidth
                      value={tempPassword}
                      type={showPassword ? 'text' : 'password'}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowPassword(!showPassword)}>
                              {showPassword ? <HideIcon /> : <ViewIcon />}
                            </IconButton>
                            <IconButton onClick={() => copyToClipboard(tempPassword)}>
                              <CopyIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Box>
          ) : (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Username"
                    value={formData.username}
                    onChange={(e) => handleInputChange('username', e.target.value)}
                    required
                    helperText="Minimum 3 characters"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Password (optional)"
                    type="password"
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    helperText="Leave empty to generate a random password"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Permissions
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_super_admin}
                        onChange={(e) => handleInputChange('is_super_admin', e.target.checked)}
                        color="error"
                      />
                    }
                    label="Super Admin (Full Access)"
                  />
                </Grid>

                {!formData.is_super_admin && (
                  <>
                    <Grid item xs={12} md={6}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.can_create_tenants}
                            onChange={(e) => handleInputChange('can_create_tenants', e.target.checked)}
                          />
                        }
                        label="Create Organizations"
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.can_delete_tenants}
                            onChange={(e) => handleInputChange('can_delete_tenants', e.target.checked)}
                          />
                        }
                        label="Delete Organizations"
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.can_modify_resources}
                            onChange={(e) => handleInputChange('can_modify_resources', e.target.checked)}
                          />
                        }
                        label="Modify Resources"
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.can_view_all_metrics}
                            onChange={(e) => handleInputChange('can_view_all_metrics', e.target.checked)}
                          />
                        }
                        label="View All Metrics"
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {tempPassword ? (
            <Button
              variant="contained"
              onClick={() => {
                setCreateDialogOpen(false);
                resetForm();
              }}
            >
              Done
            </Button>
          ) : (
            <>
              <Button
                onClick={() => {
                  setCreateDialogOpen(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleCreateAdmin}
                disabled={loading || !isFormValid()}
              >
                {loading ? <CircularProgress size={24} /> : 'Create Admin'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Edit Admin Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          resetForm();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Portal Admin</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Username"
                  value={formData.username}
                  onChange={(e) => handleInputChange('username', e.target.value)}
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="New Password (optional)"
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  helperText="Leave empty to keep current password"
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Permissions
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_super_admin}
                      onChange={(e) => handleInputChange('is_super_admin', e.target.checked)}
                      color="error"
                      disabled={editingAdmin?.email === user?.email && editingAdmin?.is_super_admin}
                    />
                  }
                  label="Super Admin (Full Access)"
                />
                {editingAdmin?.email === user?.email && editingAdmin?.is_super_admin && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    You cannot remove your own super admin status
                  </Typography>
                )}
              </Grid>

              {!formData.is_super_admin && (
                <>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.can_create_tenants}
                          onChange={(e) => handleInputChange('can_create_tenants', e.target.checked)}
                        />
                      }
                      label="Create Organizations"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.can_delete_tenants}
                          onChange={(e) => handleInputChange('can_delete_tenants', e.target.checked)}
                        />
                      }
                      label="Delete Organizations"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.can_modify_resources}
                          onChange={(e) => handleInputChange('can_modify_resources', e.target.checked)}
                        />
                      }
                      label="Modify Resources"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.can_view_all_metrics}
                          onChange={(e) => handleInputChange('can_view_all_metrics', e.target.checked)}
                        />
                      }
                      label="View All Metrics"
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditDialogOpen(false);
              resetForm();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdateAdmin}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Update Admin'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PortalAdminManagement;
